import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Typography,
  Row,
  Col,
  Image,
  Flex,
  Segmented,
  Select,
  Pagination,
} from "antd";
import CustomButton from "../../components/CustomButton/Button";
import SortIcon from "../../assets/icons/sort.svg";
import FilterIcon from "../../assets/icons/filter.svg";
import FilterDrawer from "./Components/FilterDrawer";
import SortModal from "./Components/SortModal";
import WebFilter from "./Components/WebFilter";
import "./LiveDeals.scss";
import Loader from "../../components/CustomLoader/Loader";
import DealsCard from "./Components/DealsCard";
import {
  getDealsByStatus,
  getAllEnterprises,
  getAllIndustryTypes,
  getAllRoiTypes,
  getAllMaturityTypes,
  getAllRatingTypes,
  getAllInterestStatus,
} from "../../redux/slices/DealSlice";
import { useSelector, useDispatch } from "react-redux";
import NoData from "../../components/NoData/NoData";
import { GLOBAL_CONSTANTS } from "../../constants/constants";

const { Title } = Typography;
const { Option } = Select;

const DEALS_PAGE_SIZE = 10;

const dealStatus = [
  {
    label: "Live Deals",
    value: "approved",
  },
  {
    label: "Sold Out",
    value: "agreement",
  },
  {
    label: "Settled Deals",
    value: "settled",
  },
];

const LiveDealsBanner = () => (
  <div className="live-deals-banner-container">
    <div className="banner-text">
      <Title style={{ color: "#533F02" }} level={5}>
        {GLOBAL_CONSTANTS.DEALS_BANNER_TXT1}
      </Title>
      <Title style={{ color: "#533F02" }} level={5}>
        {GLOBAL_CONSTANTS.DEALS_BANNER_TXT2}
      </Title>
    </div>
  </div>
);

const getSortOptionsForTab = (tab) => {
  if (tab === "approved") {
    return [
      { key: "highlyRec", label: "Highly Recommended" },
      { key: "newtoold", label: "New to Old" },
      { key: "hightolow", label: "High to Low (Deal Amount)" },
      { key: "lowtohigh", label: "Low to High (Deal Amount)" },
    ];
  }
  return [
    { key: "oldtonew", label: "Old to New" },
    { key: "newtoold", label: "New to Old" },
    { key: "hightolow", label: "High to Low (Deal Amount)" },
    { key: "lowtohigh", label: "Low to High (Deal Amount)" },
  ];
};

const LiveDeals = () => {
  const {
    dealsData,
    industryTypes,
    enterprises,
    roi,
    maturity,
    rating,
    loading,
    interestStatus,
  } = useSelector((state) => state.deal);

  const dispatch = useDispatch();
  const [isSortModalVisible, setIsSortModalVisible] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("approved");
  const [sortOption, setSortOption] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const getDefaultSortOption = (tab) => {
    return tab === "approved" ? "highlyRec" : "oldtonew";
  };

  useEffect(() => {
    setSortOption(getDefaultSortOption(selectedTab));
    const payload = { status: selectedTab };
    dispatch(getDealsByStatus(payload));
    dispatch(getAllEnterprises(selectedTab));
  }, [dispatch, selectedTab]);

  useEffect(() => {
    dispatch(getAllRoiTypes());
    dispatch(getAllMaturityTypes());
    dispatch(getAllRatingTypes());
    dispatch(getAllIndustryTypes());
    dispatch(getAllInterestStatus());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTab, sortOption, dealsData]);

  const sortOptions = useMemo(
    () => getSortOptionsForTab(selectedTab),
    [selectedTab],
  );

  const sortedDeals = useMemo(() => {
    return [...dealsData].sort((a, b) => {
      if (sortOption === "hightolow") {
        return b.approvedFundAmt - a.approvedFundAmt;
      } else if (sortOption === "lowtohigh") {
        return a.approvedFundAmt - b.approvedFundAmt;
      } else if (sortOption === "newtoold") {
        return new Date(b.dealApprovedDate) - new Date(a.dealApprovedDate);
      } else if (sortOption === "oldtonew") {
        return new Date(a.dealApprovedDate) - new Date(b.dealApprovedDate);
      }
      return 0;
    });
  }, [dealsData, sortOption]);

  const paginatedDeals = useMemo(() => {
    const start = (currentPage - 1) * DEALS_PAGE_SIZE;
    return sortedDeals.slice(start, start + DEALS_PAGE_SIZE);
  }, [sortedDeals, currentPage]);

  const handleOptionChange = useCallback((category, newOptions) => {
    setSelectedOptions((prev) => ({ ...prev, [category]: newOptions }));
  }, []);

  const handleSelectAll = useCallback((category, allOptions) => {
    setSelectedOptions((prev) => ({ ...prev, [category]: allOptions }));
  }, []);

  const handleClear = useCallback(() => setSelectedOptions({}), []);

  const handleApply = useCallback(() => {
    const payload = {
      status: selectedTab,
      maturityId: selectedOptions?.maturity,
      roiId: selectedOptions?.roi,
      ratingId: selectedOptions?.rating,
      industryTypeId: selectedOptions?.industryTypes,
      entId: selectedOptions?.enterprises,
    };
    dispatch(getDealsByStatus(payload));
  }, [dispatch, selectedTab, selectedOptions]);

  const handleSelectedTab = useCallback((value) => {
    setSelectedTab(value);
    setSelectedOptions({});
  }, []);

  const openSortModal = useCallback(() => setIsSortModalVisible(true), []);
  const closeSortModal = useCallback(() => setIsSortModalVisible(false), []);
  const openFilterDrawer = useCallback(() => setIsFilterDrawerOpen(true), []);
  const closeFilterDrawer = useCallback(() => setIsFilterDrawerOpen(false), []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Row align="middle" justify="space-between">
            <Col xs={12}>
              <Title level={3} style={{ margin: 0, fontSize: 24 }}>
                Explore Deals
              </Title>
            </Col>
            <Col xs={12} lg={0}>
              <Flex gap="middle" justify="flex-end">
                <CustomButton
                  type="text"
                  style={{ padding: 5, fontWeight: 500 }}
                  icon={<Image preview={false} src={SortIcon} alt="sort" />}
                  onClick={openSortModal}
                >
                  Sort
                </CustomButton>
                <CustomButton
                  type="text"
                  style={{ padding: 5, fontWeight: 500 }}
                  icon={<Image preview={false} src={FilterIcon} alt="filter" />}
                  onClick={openFilterDrawer}
                >
                  Filter
                </CustomButton>
              </Flex>
            </Col>
          </Row>

          <SortModal
            visible={isSortModalVisible}
            onClose={closeSortModal}
            onSort={setSortOption}
            sortOption={sortOption}
          />

          <FilterDrawer
            open={isFilterDrawerOpen}
            onClose={closeFilterDrawer}
            onClear={handleClear}
            onApply={handleApply}
            selectedOptions={selectedOptions}
            onOptionChange={handleOptionChange}
            onSelectAll={handleSelectAll}
            roi={roi}
            maturity={maturity}
            rating={rating}
            enterprises={enterprises}
            industryTypes={industryTypes}
          />

          <Row style={{ height: "100%", paddingTop: 20, paddingBottom: 20 }}>
            <Col xs={24} lg={19}>
              <Row align="middle" justify="space-between">
                <Col xs={24} lg={12} className="deals-container">
                  <Segmented
                    options={dealStatus}
                    value={selectedTab}
                    onChange={handleSelectedTab}
                    className="custom-segmented"
                  />
                </Col>

                <Col xs={0} lg={12} style={{ paddingRight: 20 }}>
                  <Flex align="center" justify="flex-end">
                    <Typography style={{ color: "#40566D" }}>Sort:</Typography>
                    <Select
                      value={sortOption}
                      onChange={setSortOption}
                      className="no-border-select"
                      dropdownStyle={{ width: 200 }}
                    >
                      {sortOptions.map((option) => (
                        <Option key={option.key} value={option.key}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Flex>
                </Col>
              </Row>
              {sortedDeals.length > 0 ? (
                <>
                  {paginatedDeals.map((item, index) => (
                    <React.Fragment key={item.dealId}>
                      <DealsCard deal={item} selectedTab={selectedTab} interestStatus={interestStatus} />
                      {index === 0 &&
                        currentPage === 1 &&
                        selectedTab === "approved" && <LiveDealsBanner />}
                    </React.Fragment>
                  ))}
                  {sortedDeals.length > DEALS_PAGE_SIZE && (
                    <Flex justify="center" style={{ marginTop: 24 }}>
                      <Pagination
                        current={currentPage}
                        pageSize={DEALS_PAGE_SIZE}
                        total={sortedDeals.length}
                        onChange={setCurrentPage}
                        showSizeChanger={false}
                      />
                    </Flex>
                  )}
                </>
              ) : (
                <NoData />
              )}
            </Col>

            <Col xs={0} lg={5} className="sticky-column">
              <WebFilter
                selectedOptions={selectedOptions}
                onOptionChange={handleOptionChange}
                onSelectAll={handleSelectAll}
                onClear={handleClear}
                onApply={handleApply}
                roi={roi}
                maturity={maturity}
                rating={rating}
                enterprises={enterprises}
                industryTypes={industryTypes}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default LiveDeals;
